import { z } from "zod";
import {
  fileSizeValidator,
  fileTypeValidator,
  VehiclePhotoFileSchema,
} from "./file";

// Define your service types enum (matching Laravel's ServiceType)
export const ServiceType = {
    VEHICLE_TRANSPORTATION: 'vehicle_transportation',
    VEHICLE_STORAGE: 'vehicle_storage',
    BREAKDOWN_RECOVERY: 'breakdown_recovery',
    ACCIDENT_RECOVERY: 'accident_recovery',
    PULL_OUT: 'pull_out',
    JUMP_START: 'jump_start',
    MOBILE_BATTERY_FITTING: 'mobile_battery_fitting',
    MOBILE_TYRE_FITTING: 'mobile_tyre_fitting',
    AUTO_LOCKSMITH: 'auto_locksmith',
    VEHICLE_DIAGNOSTICS: 'vehicle_diagnostics',
    MOBILE_MECHANICS: 'mobile_mechanics',
    MOBILE_VALET: 'mobile_valet',
    OUT_OF_FUEL: 'out_of_fuel',
    MISFUEL: 'misfuel',
    ADBLUE_CONTAMINATION: 'adblue_contamination',
} as const;

export type ServiceTypeValue = typeof ServiceType[keyof typeof ServiceType];

/**
 * Service types that only need a vehicle/from location.
 * Mirrors PHP's ServiceType::locationOnly().
 */
export const LOCATION_ONLY_SERVICE_TYPES: ServiceTypeValue[] = [
    ServiceType.MOBILE_MECHANICS,
    ServiceType.VEHICLE_STORAGE,
    ServiceType.AUTO_LOCKSMITH,
    ServiceType.MOBILE_BATTERY_FITTING,
    ServiceType.MOBILE_TYRE_FITTING,
    ServiceType.JUMP_START,
    ServiceType.MOBILE_VALET,
    ServiceType.VEHICLE_DIAGNOSTICS,
    ServiceType.OUT_OF_FUEL,
    ServiceType.MISFUEL,
    ServiceType.ADBLUE_CONTAMINATION,
];

export const isLocationOnlyService = (serviceType?: string): boolean =>
    LOCATION_ONLY_SERVICE_TYPES.includes(serviceType as ServiceTypeValue);

// Base question schema with all possible fields (optional by default)
const baseQuestionSchema = z.object({
    notes: z.string().max(1000).nullable().optional(),
    rolls_steers: z.boolean().optional(),
    auto_neutral_roll: z.boolean().optional(),
    modified_low_clearance: z.boolean().optional(),
    safe_location: z.boolean().optional(),
    engine_starts: z.boolean().optional(),
    keys_available: z.boolean().optional(),
    wheels_damaged: z.boolean().optional(),
    fluids_or_airbags: z.boolean().optional(),
    urgent_blocking: z.boolean().optional(),
    stuck_in: z.string().max(50).optional(),
    distance_band: z.boolean().optional(),
    truck_access_nearby: z.boolean().optional(),
    wheels_spinning: z.boolean().optional(),
    safe_to_work: z.boolean().optional(),
    can_access_vehicle: z.boolean().optional(),
    battery_location: z.boolean().optional(),
    symptoms: z.boolean().optional(),
    start_stop: z.boolean().optional(),
    duration: z.string().optional(),
    tyre_qty: z.number().int().optional(),
    tyre_positions: z.array(z.unknown()).optional(),
    tyre_size: z.boolean().optional(),
    front_size_raw: z.string().max(50).optional(),
    run_flat: z.boolean().optional(),
    tyre_tier: z.string().max(50).optional(),
    locking_nut: z.boolean().optional(),
    safe_access: z.boolean().optional(),
    battery_type: z.string().max(50).optional(),
    service_level: z.string().max(50).optional(),
    issue_type: z.string().max(50).optional(),
    access_situation: z.string().max(50).optional(),
    proof_of_ownership: z.boolean().optional(),
    key_type: z.boolean().optional(),
    replacement_key: z.boolean().optional(),
    programming_needed: z.boolean().optional(),
    primary_concern: z.string().max(50).optional(),
    report_required: z.boolean().optional(),
    report_detail: z.string().optional(),
    problem_category: z.string().max(50).optional(),
    drivable: z.boolean().optional(),
    diagnosed_already: z.boolean().optional(),
    obd_codes: z.boolean().optional(),
    package: z.string().max(50).optional(),
    contamination: z.string().max(50).optional(),
    special_issues: z.array(z.unknown()).optional(),
    required_fuel_type: z.string().max(50).optional(),
    approx_fuel_litres: z.coerce.number().optional(),
    safe_position_confirmed: z.boolean().optional(),
    wrong_fuel_type: z.string().max(50).optional(),
    correct_fuel_type: z.string().max(50).optional(),
    approx_wrong_fuel_litres: z.coerce.number().optional(),
    ignition_switched_on: z.boolean().optional(),
    engine_started_after_misfuel: z.boolean().optional(),
    vehicle_driven_after_misfuel: z.boolean().optional(),
    location_type: z.string().max(50).optional(),
    fuel_supplied_litres: z.coerce.number().optional(),
    fuel_supplied_cost: z.coerce.number().optional(),
    vehicle_restarted: z.boolean().optional(),
});

// Human-readable labels for every question field
export const fieldLabels: Record<string, string> = {
    rolls_steers: 'Please indicate whether the vehicle can roll and steer',
    auto_neutral_roll: 'Please indicate whether the automatic transmission will go into neutral',
    modified_low_clearance: 'Please indicate whether the vehicle has low clearance or modifications',
    keys_available: 'Please indicate whether keys are available at pickup',
    safe_location: 'Please indicate whether the vehicle is in a safe location',
    engine_starts: 'Please indicate whether the engine can start',
    wheels_damaged: 'Please indicate whether any wheels are damaged or not rotating',
    fluids_or_airbags: 'Please indicate whether there is fluid leakage or deployed airbags',
    urgent_blocking: 'Please indicate whether the vehicle is blocking the road',
    stuck_in: 'Please describe what the vehicle is stuck in (mud, snow, ditch, etc.)',
    distance_band: 'Please indicate whether the vehicle is more than 5m off road',
    truck_access_nearby: 'Please indicate whether a recovery truck can access within 5–10m',
    wheels_spinning: 'Please indicate whether the wheels are spinning freely',
    safe_to_work: 'Please indicate whether it is safe to work at the location',
    can_access_vehicle: 'Please indicate whether you can access the vehicle',
    battery_location: 'Please indicate whether the battery is accessible under the bonnet',
    symptoms: 'Please indicate whether there are any visible symptoms (smoke, leaks, warning lights)',
    start_stop: 'Please indicate whether the vehicle is start-stop',
    duration: 'Please indicate whether this is short-term storage (under 30 days)',
    tyre_qty: 'Please enter the number of tyres that need fitting',
    tyre_positions: 'Please select the tyre positions that need fitting',
    tyre_size: 'Please indicate whether you know your tyre size',
    front_size_raw: 'Please enter your tyre size (e.g. 205/55 R16)',
    run_flat: 'Please indicate whether run-flat tyres are required',
    locking_nut: 'Please indicate whether a locking wheel nut is fitted',
    tyre_tier: 'Please select your tyre/brand tier preference',
    safe_access: 'Please indicate whether the vehicle is in a safe and accessible location',
    battery_type: 'Please enter the battery type',
    service_level: 'Please select your service level preference',
    issue_type: 'Please describe the locksmith issue',
    access_situation: 'Please describe the vehicle access situation',
    proof_of_ownership: 'Please indicate whether you can provide proof of ownership',
    key_type: 'Please indicate the type of key (smart / transponder)',
    replacement_key: 'Please indicate whether a replacement key is required',
    programming_needed: 'Please indicate whether the key needs programming or coding',
    primary_concern: 'Please describe your primary concern',
    report_required: 'Please indicate whether a written diagnostic report is required',
    problem_category: 'Please select the problem category',
    drivable: 'Please indicate whether the vehicle is currently drivable',
    diagnosed_already: 'Please indicate whether the vehicle has been diagnosed elsewhere',
    obd_codes: 'Please indicate whether you have any OBD fault codes',
    package: 'Please select the valet package you require',
    contamination: 'Please describe any heavy contamination (pet hair, smoke, stains)',
    special_issues: 'Please list any special issues with the vehicle',
    report_detail: 'Please provide details for the diagnostic report',
    required_fuel_type: 'Please select the fuel type required',
    approx_fuel_litres: 'Please select the approximate litres needed',
    safe_position_confirmed: 'Please confirm the vehicle is in a safe position',
    wrong_fuel_type: 'Please select the wrong fuel that was added',
    correct_fuel_type: 'Please select the correct fuel for the vehicle',
    approx_wrong_fuel_litres: 'Please select the approximate amount of wrong fuel put in',
    ignition_switched_on: 'Please indicate whether the ignition was switched on',
    engine_started_after_misfuel: 'Please indicate whether the engine was started after misfuel',
    vehicle_driven_after_misfuel: 'Please indicate whether the vehicle was driven after misfuel',
    location_type: 'Please select the location type',
};

export const bookingStepSchema = z.object({
  service_type: z.enum([
    ServiceType.VEHICLE_TRANSPORTATION,
    ServiceType.VEHICLE_STORAGE,
    ServiceType.BREAKDOWN_RECOVERY,
    ServiceType.ACCIDENT_RECOVERY,
    ServiceType.PULL_OUT,
    ServiceType.JUMP_START,
    ServiceType.MOBILE_BATTERY_FITTING,
    ServiceType.MOBILE_TYRE_FITTING,
    ServiceType.AUTO_LOCKSMITH,
    ServiceType.VEHICLE_DIAGNOSTICS,
    ServiceType.MOBILE_MECHANICS,
    ServiceType.MOBILE_VALET,
    ServiceType.OUT_OF_FUEL,
    ServiceType.MISFUEL,
    ServiceType.ADBLUE_CONTAMINATION,
  ]),
});

export const bookingStep1Schema = z
  .object({
    vehicle_registration: z.string().optional(),
    vehicle_type: z.string().optional(),
    vehicle_make: z.string({ message: "Vehicle make is required" }),
    vehicle_model: z.string({ message: "Vehicle model is required" }),
    vehicle_colour: z
      .string({ message: "Vehicle colour is required" })
      .optional(),
    vehicle_fuel_type: z
      .string({ message: "Vehicle fuel type is required" })
      .optional(),
    vehicle_year: z.coerce
      .number({ message: "Vehicle year is required" })
      .optional(),
    vehicle_category: z.enum(["under_3.5t", "3.5-7.5t", "over_7.5t"], {
      message: "Vehicle category is required",
    }),
    vehicle_body_type: z.string({ message: "Vehicle body type is required" }),
    vehicle_weight: z.string().optional(),
    vehicle_transmission: z.string().optional(),
    vehicle_wheelbase: z.string().optional(),
    vehicle_image_path: z.string().optional(),
    vehicle_mot_status: z.string().optional(),
    vehicle_expiry_date: z.string().optional(),
    vehicle_tax_status: z.string().optional(),
    vehicle_tax_due_date: z.string().optional(),
  })
  .refine(
    (data) => {
      if (
        !data.vehicle_make &&
        !data.vehicle_model &&
        !data.vehicle_colour &&
        !data.vehicle_fuel_type &&
        !data.vehicle_year &&
        !data.vehicle_category &&
        !data.vehicle_body_type
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Please enter at least one vehicle detail",
      path: ["vehicle_registration"],
    },
  );

/**
 * Step 2 schema factory.
 * For location-only service types the destination (to_*) fields are optional —
 * only the vehicle/from location is needed.
 */
export const getBookingStep2Schema = (serviceType?: string) => {
    const locationOnly = isLocationOnlyService(serviceType);

    const toAddress = locationOnly
        ? z.string().nullable().optional()
        : z.string().min(1, "Drop-off address is required");

    const toLatLng = locationOnly
        ? z.number().nullable().optional()
        : z.number({ message: "Drop-off location is required" });

    return z.object({
        from_address: z.string().min(1, "Vehicle location address is required"),
        from_latitude: z.number({ message: "Vehicle location is required" }),
        from_longitude: z.number({ message: "Vehicle location is required" }),
        to_address: toAddress,
        to_latitude: toLatLng,
        to_longitude: toLatLng,
    });
};

// Static fallback (assumes destination is required — use getBookingStep2Schema for dynamic validation)
export const bookingStep2Schema = getBookingStep2Schema();

export const bookingStep3Schema = z
  .object({
    vehicle_image: VehiclePhotoFileSchema.refine(
      (file) => fileSizeValidator(file),
      "Max image size is 5MB.",
    ).optional(),
    scheduled_at: z
      .date({
        message: "Collection date is required",
      })
      .nullable(),
    passenger_count: z.number().min(0),
    pet_count: z.number().min(0),
  })
  .refine((data) => fileTypeValidator(data.vehicle_image), {
    message: "Only .jpg, .jpeg, .png, and .webp formats are accepted.",
    path: ["vehicle_image"],
  });

export const bookingStep4Schema = z.object({
    service_type: z.enum([
        ServiceType.VEHICLE_TRANSPORTATION,
        ServiceType.VEHICLE_STORAGE,
        ServiceType.BREAKDOWN_RECOVERY,
        ServiceType.ACCIDENT_RECOVERY,
        ServiceType.PULL_OUT,
        ServiceType.JUMP_START,
        ServiceType.MOBILE_BATTERY_FITTING,
        ServiceType.MOBILE_TYRE_FITTING,
        ServiceType.AUTO_LOCKSMITH,
        ServiceType.VEHICLE_DIAGNOSTICS,
        ServiceType.MOBILE_MECHANICS,
        ServiceType.MOBILE_VALET,
        ServiceType.OUT_OF_FUEL,
        ServiceType.MISFUEL,
        ServiceType.ADBLUE_CONTAMINATION,
    ]),
    questions: z.array(baseQuestionSchema),
    payment_method: z.enum(['card', 'cash']).optional(),
    inspection_required: z.boolean().optional(),
    inspection_tier: z.enum(['basic', 'enhanced', 'enhanced_obd']).optional(),
    inspection_template_id: z.number().optional(),
}).superRefine((data, ctx) => {
    const serviceType = data.service_type;

    // Validate each question in the array
    data.questions.forEach((question, index) => {
        // Conditional required validations
        const validations = [
            {
                condition: serviceType === ServiceType.VEHICLE_TRANSPORTATION,
                fields: ['rolls_steers', 'auto_neutral_roll', 'modified_low_clearance', 'keys_available']
            },
            {
                condition: serviceType === ServiceType.BREAKDOWN_RECOVERY,
                fields: ['rolls_steers', 'safe_location', 'engine_starts', 'keys_available']
            },
            {
                condition: serviceType === ServiceType.ACCIDENT_RECOVERY,
                fields: ['rolls_steers', 'auto_neutral_roll', 'wheels_damaged', 'fluids_or_airbags', 'urgent_blocking']
            },
            {
                condition: serviceType === ServiceType.PULL_OUT,
                fields: ['stuck_in', 'distance_band', 'truck_access_nearby', 'wheels_spinning', 'safe_to_work']
            },
            {
                condition: serviceType === ServiceType.JUMP_START,
                fields: ['can_access_vehicle', 'battery_location', 'symptoms', 'start_stop']
            },
            {
                condition: serviceType === ServiceType.VEHICLE_STORAGE,
                fields: ['rolls_steers', 'keys_available', 'duration']
            },
            {
                condition: serviceType === ServiceType.MOBILE_TYRE_FITTING,
                fields: ['tyre_qty', 'tyre_positions', 'tyre_size', 'tyre_tier']
            },
            {
                condition: serviceType === ServiceType.MOBILE_BATTERY_FITTING,
                fields: ['tyre_tier', 'safe_access']
            },
            {
                condition: serviceType === ServiceType.AUTO_LOCKSMITH,
                fields: ['issue_type', 'access_situation', 'proof_of_ownership', 'key_type', 'replacement_key', 'programming_needed']
            },
            {
                condition: serviceType === ServiceType.VEHICLE_DIAGNOSTICS,
                fields: ['primary_concern', 'report_required', 'safe_access']
            },
            {
                condition: serviceType === ServiceType.MOBILE_MECHANICS,
                fields: ['problem_category', 'drivable', 'diagnosed_already', 'obd_codes']
            },
            {
                condition: serviceType === ServiceType.MOBILE_VALET,
                fields: ['package', 'contamination', 'special_issues']
            },
            {
                condition: serviceType === ServiceType.OUT_OF_FUEL,
                fields: ['required_fuel_type', 'approx_fuel_litres', 'safe_position_confirmed']
            },
            {
                condition: serviceType === ServiceType.MISFUEL,
                fields: ['wrong_fuel_type', 'correct_fuel_type', 'approx_wrong_fuel_litres', 'ignition_switched_on', 'engine_started_after_misfuel', 'vehicle_driven_after_misfuel', 'location_type', 'safe_position_confirmed']
            },
            {
                condition: serviceType === ServiceType.ADBLUE_CONTAMINATION,
                fields: ['location_type', 'safe_position_confirmed']
            },
        ];

        // Apply conditional required validations
        validations.forEach(({ condition, fields }) => {
            if (condition) {
                fields.forEach(field => {
                    const value = (question as Record<string, unknown>)[field];
                    if (
                      value === undefined ||
                      value === null ||
                      value === ""
                    ) { 
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: fieldLabels[field] ?? `${field} is required`,
                        path: ["questions", index, field],
                      });
                    }
                });
            }
        });

        // Special case for report_detail which depends on report_required
        if (question.report_required === true && question.report_detail === undefined || question.report_detail === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: fieldLabels['report_detail'],
                path: ['questions', index, 'report_detail']
            });

        }
    });
});


/**
 * Full booking schema factory — pass the chosen service type so the
 * destination fields are only required when relevant.
 */
export const getCreateBookingSchema = (serviceType?: string) =>
    bookingStepSchema
        .merge(bookingStep1Schema)
        .merge(getBookingStep2Schema(serviceType))
        .merge(bookingStep3Schema)
        .merge(bookingStep4Schema);

// Static fallback (destination always required)
export const createBookingSchema = getCreateBookingSchema();

export type BookingStepData = z.infer<typeof bookingStepSchema>;
export type BookingStep1Data = z.infer<typeof bookingStep1Schema>;
export type BookingStep2Data = z.infer<typeof bookingStep2Schema>;
export type BookingStep3Data = z.infer<typeof bookingStep3Schema>;
export type BookingStep4Data = z.infer<typeof bookingStep4Schema>;
export type CreateBookingData = z.infer<typeof createBookingSchema>;
